import { Box, Stack, Typography } from '@mui/material'
import { Link } from '@mui/material'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { IconButton, Icons } from 'utoniq-ui'

import {
  Maybe,
  PortfolioConnectionFragment,
} from '@/clients/utoniq-core/schema'
import { QRCodeBox } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'

import { Props } from './OwnedItemDetailWallpaperView'

type KonbiniPrintModalProps = {
  QRCodePrinting: boolean
  onClose: () => void
  onGenerateWallpaperPrintQrcode: Props['onGenerateWallpaperPrintQrcode']

  portfolio?: Maybe<
    PortfolioConnectionFragment & {
      qrCodeUri?: string
      completedAt?: boolean
      serial?: string
      metadata?: {
        wallpaper: {
          print: {
            expiredAt: string
          }
        }
      }
    }
  >
}

export const KonbiniPrintModal: Organism<KonbiniPrintModalProps> = ({
  QRCodePrinting,
  portfolio,
  onClose,
  onGenerateWallpaperPrintQrcode,
}) => {
  const { t } = useTranslation('ownedItemDetail')

  return (
    <OrganismFrame
      sx={{
        bgcolor: 'background.default',
        height: '90dvh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" sx={{ py: 4, px: 3 }} alignItems="center">
        <Box width={44} />
        <Typography variant="h6" align="center" width="100%">
          {t('printingContent', 'コンビニプリント内容')}
        </Typography>
        <IconButton.Close
          size="small"
          onClick={onClose}
          sx={{ width: 44, height: 44, p: '12px' }}
        />
      </Stack>
      <Stack
        flex={1}
        sx={{ py: 4, overflowY: 'auto', ...styleSets.hideScrollbar() }}
      >
        <Stack sx={{ px: 5 }}>
          <Link
            alignItems="center"
            component="a"
            variant="caption"
            underline="none"
            target="_blank"
            display="flex"
            href="https://help.utoniq.com/ja/articles/10329377"
          >
            <Typography variant="caption" color="primary.main">
              {t('printMethod')}
            </Typography>
            <Icons.Help color="primary" sx={{ height: 12, width: 12, ml: 1 }} />
          </Link>
          <Stack direction="row" alignItems="center" spacing={2} pt={3}>
            <Icons.OwnedItems />
            <Typography variant="h6">
              {t('numberOfSheet', { sheet: portfolio?.serials?.length ?? 0 })}
            </Typography>
          </Stack>
        </Stack>
        <QRCodeCell
          QRCodePrinting={QRCodePrinting}
          serials={portfolio?.serials}
          item={portfolio?.item}
          onGenerateWallpaperPrintQrcode={onGenerateWallpaperPrintQrcode}
        />
      </Stack>
    </OrganismFrame>
  )
}

type QRCodeCellProps = {
  QRCodePrinting: boolean
  item?: Maybe<PortfolioConnectionFragment['item']>
  serials?: Maybe<PortfolioConnectionFragment['serials']>
  onGenerateWallpaperPrintQrcode: Props['onGenerateWallpaperPrintQrcode']
}
const QRCodeCell: Cell<QRCodeCellProps> = ({
  QRCodePrinting,
  item,
  serials,
  onGenerateWallpaperPrintQrcode,
}) => {
  return (
    <CellFrame
      sx={{
        py: 4,
        '.swiper': {
          px: 5,
          maxWidth: '100%',
        },
        '.swiper-pagination-bullet-active': {
          bgcolor: 'grey.700',
        },
      }}
    >
      <Swiper
        slidesPerView="auto"
        spaceBetween={20}
        modules={[Pagination]}
        pagination={{
          type: 'bullets',
          clickable: true,
        }}
        enabled={!!serials && serials.length > 1}
        style={{ paddingBottom: 40 }}
      >
        {serials?.map((serial, index) => (
          <SwiperSlide key={index} style={{ width: 315 }}>
            <QRCodeBox
              loading={QRCodePrinting}
              item={item}
              serial={serial}
              onGenerateWallpaperPrintQrcode={onGenerateWallpaperPrintQrcode}
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </CellFrame>
  )
}
